/**
 * What colour the sea is — the ocean's `enhanced-sky-look.ts`, and it borrows
 * from that file rather than restating it.
 *
 * `ocean-waves.ts` says where the surface is; `three/ocean-material.ts` draws
 * it. This decides which `BARON.PAL` entries the result is allowed to be, how
 * far a wave face and a shoal may walk along them, and how much of the sun the
 * water is allowed to throw back.
 *
 * ## The palette rule, and why it is stricter than the sky's
 *
 * The whole sea, everywhere except inside the sun's specular core, is **exactly
 * one of `WATER_RAMP`'s seven entries** — `BARON.PAL` 1-7, the game's own water
 * run with the measured sea colour 4 `#304965` at its centre
 * (`world/terrain-palette.ts`). None of the three things that vary it is a
 * blend: a wave face, a shoal and the sun's broad path each move the *ramp
 * position*, an integer, and the colour is then read out of the run. So an ocean
 * pixel away from the highlight is a palette byte, not a number near one, and
 * `ocean-palette.test.ts` plus the live census in `docs/engineering/ocean.md`
 * both check it that way — 96.7 % exact entries on a real low-pass frame.
 *
 * That is stricter than `enhanced-sky.ts`, which mixes its four gradient stops
 * and puts the result back on the 6-bit DAC grid. The sky can afford it — a
 * gradient is what a sky *is*. A flat plate of water cannot: this port's own
 * 2026-09-01 rule (`lessons.md`, "an opaque mesh that fades to pale") is that a
 * large opaque surface is judged by its area-weighted average, and the sea is
 * the largest opaque surface in the game. That was measured rather than
 * reasoned about — see `SUN_PATH_STEPS`.
 *
 * ## The specular core is the one place the ramp is left, and it is the sky's own light
 *
 * The tight highlight is *radiance*, exactly as `enhanced-sky.ts`'s glare is,
 * and it is built out of the same palette entry that hour's sky already glares
 * with — so the light on the water and the light in the sky cannot end up
 * different colours. Both it and the broad path are scaled by the same
 * `SKY_WORD_TREATMENTS.glareScale` the dome uses, which is what makes an
 * overcast sea dull without a second table saying so, and it goes through
 * `enhanced-sky.ts`'s own `toneMap` and `three/ordered-dither.ts`'s DAC
 * quantiser, so what leaves the shader is on the grid the palette itself lives
 * on.
 *
 * ## Night is the moon's, and the ramp has nowhere left to go
 *
 * `celestialBodyFor` hands back the **moon** once `sun.c`'s own `sin(angle) < 0`
 * test has put the sun away, so the reflection at night hangs under the body
 * that is actually up there — the same substitution `sky-dressing.ts` makes for
 * the dome's halo. And the hour has already walked the water down its run by
 * then: `groundRampShiftFor`'s night shift is −4 against a centre of 3, so
 * `baseIndex` clamps at 0 and the sea sits on `BARON.PAL` 1 `#203449` with no
 * room to darken further. The wave shading is therefore one-sided at night —
 * facets can only catch light — which is what a dark sea under a moon does
 * anyway, and it is a consequence of the ramp rather than a rule anyone wrote.
 */

import type { SkyCondition, TimeOfDay, Vec3 } from "@red-opus/engine";
import { celestialBodyFor } from "./time-of-day.ts";
import { lobeExponentForHalfAngle, SKY_WORD_TREATMENTS, STOPS_BY_HOUR } from "./enhanced-sky-look.ts";
import { shiftRampIndex, WATER_RAMP, type PaletteColour } from "../world/terrain-palette.ts";

/**
 * How far up the run the water walks where it is shallowest, in ramp steps.
 *
 * Three, which from the centre 3 reaches `WATER_RAMP`'s top entry 7 `#416582` —
 * the lightest water the game ships, and the one it paints its own shallowest
 * river reaches with. Chosen as "the end of the run" rather than as a taste
 * value: a shoal that stops short of the run's end is an arbitrary number, and a
 * shoal that walks past it would need a colour `BARON.PAL` does not have.
 */
export const SHORE_RAMP_LIFT = 3;

/**
 * How far a wave face may swing the ramp position either side of its base, in
 * steps.
 *
 * Two. `ocean-waves.ts` bounds the surface slope at 12.7°, and
 * `world/terrain-palette.ts`'s `terrainShadeLevel` — the original's own
 * `floor(16·cos)` quantiser — resolves 3.6° near the horizon, so a ±2 swing over
 * a 12.7° range gives a wave face about three distinguishable tones. One is a
 * flat plate with a shore rim; three starts to read as corduroy, because every
 * face in the near field would be at an end stop.
 */
export const WAVE_SHADE_STEPS = 2;

/**
 * How far up the run the sun's *broad* reflection walks the water, in ramp
 * steps — the glitter road, as paint rather than as light.
 *
 * **This is the rule the palette claim rests on, and it was learned by
 * measuring.** The first build put a broad radiance wash around the specular
 * core, the way `enhanced-sky.ts` does for the sky. On a low pass with the sun
 * high the wash's lobe covers nearly the whole surface, so it lifted every sea
 * pixel by about 0.09 of display white and a real frame came back **93.6 %
 * off-palette** — a washed-out sea in colours `BARON.PAL` does not contain.
 *
 * A sky can afford that: a gradient is what a sky is. A flat plate of water
 * cannot. So anything that changes the sea's tone *broadly* moves the ramp
 * position — an integer, resolved between two real entries by the port's own
 * stipple — and only the tight specular core is allowed to be radiance. Two
 * steps takes deep water from the centre 4 `#304965` to the top 6 `#385d75` in
 * the sun's bearing, which is a visible road in the game's own vocabulary.
 */
export const SUN_PATH_STEPS = 2;

/**
 * How much of the sky's own glare radiance the water throws back on the sharpest
 * facet it has. **INVENTED** — declared here rather than buried, per
 * `enhanced-models.md` §4.3.
 *
 * There is no physical chain available to derive it: `enhanced-sky-look.ts`'s
 * `glareCoreGain` is already a compressed number describing a *halo* rather than
 * the sun's true radiance, so a Fresnel reflectance applied to it would produce
 * a glitter path two orders of magnitude too dim. It is expressed as a share of
 * that gain rather than as an absolute so the two move together: a lane that
 * retunes the sky's glare retunes the water's without having to know this file
 * exists.
 */
export const GLITTER_SHARE = 1.1;

/**
 * The Fresnel floor — how much of the glitter survives at a straight-down view,
 * where a real water surface reflects about 2 % and would show nothing at all.
 *
 * The *shape* is Schlick's and it is doing real work: reflection rises to 1 at
 * grazing incidence, which is why the glitter path from a cockpit is brightest
 * toward the horizon and fades under the nose. The floor is what stops the same
 * curve deleting it directly below, in the compressed units the paragraph above
 * describes.
 */
export const FRESNEL_FLOOR = 0.35;
/** Schlick's `F0` for water at normal incidence — `((1 − 1.333)/(1 + 1.333))²`, the one number here that is not a choice. */
export const WATER_F0 = ((1 - 1.333) / (1 + 1.333)) ** 2;

/**
 * Half-angle of the specular lobe on water whose finest octaves are fully
 * resolved, and on water where they have been filtered away by range.
 *
 * The pair exists because of what happens to glitter at altitude if it does not.
 * The fine octaves are filtered out as a wave gets smaller than a pixel
 * (`three/ocean-material.ts`), and filtering a *normal* away does not remove the
 * light it was catching — it turns a narrow lobe into a wide one. Dropping the
 * lobe with the normal makes the sea go glassy and dead exactly where a pilot
 * spends the sortie; broadening it instead keeps the glitter road on screen at
 * patrol height, which is the whole reason the ocean reads as water from up
 * there.
 */
export const GLITTER_SHARP_HALF_ANGLE_DEG = 1.4;
/**
 * What share of the core's peak radiance survives once the lobe has broadened
 * all the way. Broadening a lobe at constant peak adds energy rather than
 * conserving it, and a 20° lobe at full gain is a blaze the size of the sea; a
 * third of it is bright without being a second sky.
 */
export const BROAD_GAIN_SHARE = 0.3;
/**
 * 20°, and it is sized against the sea's own slope statistics rather than
 * picked. `ocean-waves.ts`'s RMS slope is 0.056 rad = 3.2°, and a facet tilted
 * by θ swings its reflection by 2θ, so the *surface* already spreads the sun by
 * about 6.4° on its own. A glitter road seen from an aeroplane spans some 30°;
 * an 11° lobe (the first value tried) put the whole reflection in a strip a few
 * pixels deep along the horizon, which is where the specular direction lands
 * when the sun is 7.5° up. 20° is what carries the road out of the horizon and
 * toward the viewer.
 */
export const GLITTER_BROAD_HALF_ANGLE_DEG = 20;

/** Everything one sortie's sea is drawn from. */
export interface OceanLook {
  /** Where on `WATER_RAMP` deep, level, unlit water sits at this hour. */
  readonly baseIndex: number;
  /** The run itself, so the shader and the tests read one array. */
  readonly ramp: readonly PaletteColour[];
  /** Unit direction to the body the glitter hangs under — the sun by day, the moon after `sun.c` puts it away. */
  readonly sun: Vec3;
  /** The tight glitter's colour and peak radiance, in units of display white — the one term that leaves the run. */
  readonly glitterCore: PaletteColour;
  readonly glitterCoreGain: number;
  /** The broad reflection, as ramp steps rather than radiance — see `SUN_PATH_STEPS`. Zero once a sky word has taken the sun away. */
  readonly sunPathSteps: number;
  readonly timeOfDay: TimeOfDay;
  readonly sky: SkyCondition;
}

/** Straight up — the bearing a sortie that briefs no weather gets, matching `enhanced-sky.ts`'s own stand-in. */
const OVERHEAD: Vec3 = { x: 0, y: 1, z: 0 };

/**
 * One sortie's brief, as the sea needs it.
 *
 * The glitter's colours are **not** in here, deliberately: they are read off
 * `enhanced-sky-look.ts`'s own `STOPS_BY_HOUR`, so the light on the water and
 * the light in the sky cannot be given two different tables to drift apart in.
 */
export interface OceanBrief {
  readonly timeOfDay: TimeOfDay | undefined;
  readonly sky: SkyCondition | undefined;
  /** `flight/time-of-day.ts`'s `groundRampShiftFor` — the walk the rest of the terrain has already taken for this hour. */
  readonly rampShift: number;
}

export const DEFAULT_OCEAN_HOUR: TimeOfDay = "daytime";
export const DEFAULT_OCEAN_SKY: SkyCondition = "clear";

/**
 * The sortie's sea.
 *
 * `baseIndex` is `shiftRampIndex(WATER_RAMP, WATER_RAMP.centre, rampShift)` and
 * that is not a resemblance — it is the *identical* call
 * `world/terrain-palette.ts`'s `dimTerrainColour` makes when
 * `three/world-shared-ref.ts` walks the plate's own vertex colours down for the
 * hour. So the ocean's deep water and the flat sea it replaces are the same
 * palette entry at every hour, by construction rather than by a table that has
 * to be kept in step.
 */
export function oceanLookFor(brief: OceanBrief): OceanLook {
  const hour = brief.timeOfDay ?? DEFAULT_OCEAN_HOUR;
  const word = brief.sky ?? DEFAULT_OCEAN_SKY;
  // The sky's own stops for this hour, un-greyed — `greyedStops` washes the
  // gradient toward the overcast grey and deliberately leaves the glare alone,
  // because cloud in front of the sun takes light away rather than turning it
  // grey. The water is on exactly the same terms, and takes it away with the
  // same `glareScale`.
  const stops = STOPS_BY_HOUR[hour];
  const glareScale = SKY_WORD_TREATMENTS[word].glareScale;
  return {
    baseIndex: shiftRampIndex(WATER_RAMP, WATER_RAMP.centre, brief.rampShift),
    ramp: WATER_RAMP.colours,
    sun: celestialBodyFor(brief.timeOfDay)?.direction ?? OVERHEAD,
    glitterCore: stops.glareCore,
    glitterCoreGain: stops.glareCoreGain * GLITTER_SHARE * glareScale,
    sunPathSteps: SUN_PATH_STEPS * glareScale,
    timeOfDay: hour,
    sky: word,
  };
}

/** The two lobe exponents the shader interpolates between, solved back from the stated half-angles. */
export function glitterLobeExponents(): { readonly sharp: number; readonly broad: number } {
  return {
    sharp: lobeExponentForHalfAngle(GLITTER_SHARP_HALF_ANGLE_DEG),
    broad: lobeExponentForHalfAngle(GLITTER_BROAD_HALF_ANGLE_DEG),
  };
}

/**
 * Schlick's reflectance with this module's floor applied — the scalar twin of
 * what the shader computes, so `ocean-look.test.ts` can pin the two ends
 * (straight down and grazing) without a GPU.
 */
export function fresnelGain(cosineToNormal: number): number {
  const cosine = Math.min(Math.max(cosineToNormal, 0), 1);
  const schlick = WATER_F0 + (1 - WATER_F0) * (1 - cosine) ** 5;
  return FRESNEL_FLOOR + (1 - FRESNEL_FLOOR) * schlick;
}

/**
 * The ramp position a piece of sea lands on, given its wave shade and how
 * shallow it is — the shader's integer walk, in plain arithmetic.
 *
 * `shade` is `−1 .. 1` (the reflected-elevation term) and `shoal` is `0 .. 1`
 * (1 at the waterline). **One sum, then one floor**, exactly as
 * `three/ocean-material.ts` writes it: rounding the two terms separately would
 * lose the fractional part the stipple is made of. `dither` is
 * `ordered-dither.ts`'s Bayer threshold at the fragment — its default of a half
 * makes this an ordinary round, which is what a caller reasoning about a whole
 * face wants. Clamped to the run, which is what makes the night case in the
 * header's last paragraph a consequence rather than a special case.
 */
export function oceanRampIndex(look: OceanLook, shade: number, shoal: number, sunPath = 0, dither = 0.5): number {
  const walked =
    look.baseIndex +
    Math.min(Math.max(shade, -1), 1) * WAVE_SHADE_STEPS +
    Math.min(Math.max(shoal, 0), 1) * SHORE_RAMP_LIFT +
    Math.min(Math.max(sunPath, 0), 1) * look.sunPathSteps;
  return Math.max(0, Math.min(look.ramp.length - 1, Math.floor(walked + dither)));
}
